/**
 * Self-test suite for the web screen lock (passphrase + WebAuthn policy +
 * throttling).
 *
 * Mirrors iOS `AppLockSelfTest.swift` case-for-case and adds web-specific
 * cases (algorithm discriminator, IndexedDB persistence, WebAuthn policy
 * semantics).
 *
 * Groups:
 *   A. Passphrase hash    (1–6)   — hash + verify, length validation, salt size
 *   B. Lockout policy     (7–10)  — table lookup for throttle intervals
 *   C. State machine      (11–21) — every transition rule
 *   D. Store              (22–25) — IndexedDB round-trips, persistence, defaults
 *   E. Hardening          (26–27) — constant-time verify, wipePending terminal
 *   F. Algorithm guard    (28)    — unsupported discriminator → explicit error
 *
 * Hit pure-crypto and pure-logic paths only.  The IndexedDB store cases use
 * isolated database names so suites don't collide.
 *
 * Registered with the global SelfTestRunner at module load time; auto-
 * discovered by Vite `import.meta.glob` in `selftest/index.ts`.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";

import {
  hashPassphrase,
  verifyPassphrase,
  generateSalt,
  DEFAULT_PARAMS,
  PassphraseTooShortError,
  PassphraseTooLongError,
  UnsupportedAlgorithmError,
  type AppLockPassphraseHash,
} from "./AppLockPin";

import {
  transition,
  initialStatus,
  ZERO_HISTORY,
  LOCKOUT_POLICY,
  nextLockoutMs,
  setBackgroundLockMs,
  BIOMETRIC_24H_WINDOW_MS,
  type AppLockStatus,
  type AppLockEvent,
} from "./AppLockState";

import { AppLockStore } from "./AppLockStore";

// ---------------------------------------------------------------------------
// Test helpers
// ---------------------------------------------------------------------------

/**
 * PBKDF2 at production iteration count (600k) would make the suite take
 * ~20–40 s in the browser. The crypto envelope is identical with a low
 * count; we are testing *behaviour* (round-trips, errors) not parameter
 * strength.
 */
const FAST_PARAMS = { algorithm: "PBKDF2-SHA256" as const, iterations: 1_000 };

/** Each store test gets an isolated database so they don't clobber each other. */
let _storeSeq = 0;
function freshStore(): AppLockStore {
  return new AppLockStore(`kaether_applock_test_${_storeSeq++}`);
}

// ---------------------------------------------------------------------------
// A. Passphrase hash (cases 1–6)
// ---------------------------------------------------------------------------

/** 1. Hash + verify round-trip with the same passphrase. */
async function testHashRoundTrip(): Promise<TestCase> {
  const name = "hash + verify round-trip with same passphrase";
  try {
    const salt = generateSalt();
    const h = await hashPassphrase("hunter2!", salt, FAST_PARAMS);
    const ok = await verifyPassphrase("hunter2!", h);
    return ok ? pass(name) : fail(name, "verifyPassphrase returned false on correct passphrase");
  } catch (e) {
    return fail(name, String(e));
  }
}

/** 2. Passphrase < 6 chars → PassphraseTooShortError. */
async function testTooShort(): Promise<TestCase> {
  const name = "passphrase < 6 chars is rejected";
  try {
    await hashPassphrase("abc", generateSalt(), FAST_PARAMS);
    return fail(name, "hashPassphrase did not throw");
  } catch (e) {
    if (e instanceof PassphraseTooShortError) return pass(name);
    return fail(name, `expected PassphraseTooShortError, got ${e}`);
  }
}

/** 3. Passphrase > 64 chars → PassphraseTooLongError. */
async function testTooLong(): Promise<TestCase> {
  const name = "passphrase > 64 chars is rejected";
  const long = "a".repeat(65);
  try {
    await hashPassphrase(long, generateSalt(), FAST_PARAMS);
    return fail(name, "hashPassphrase did not throw");
  } catch (e) {
    if (e instanceof PassphraseTooLongError) return pass(name);
    return fail(name, `expected PassphraseTooLongError, got ${e}`);
  }
}

/** 4. Wrong passphrase → verifyPassphrase returns false (not throw). */
async function testWrongPassphrase(): Promise<TestCase> {
  const name = "wrong passphrase returns false (not throw)";
  try {
    const salt = generateSalt();
    const h = await hashPassphrase("correct-horse", salt, FAST_PARAMS);
    const bad = await verifyPassphrase("wrong-horse!!", h);
    return bad ? fail(name, "verifyPassphrase returned true on wrong passphrase") : pass(name);
  } catch (e) {
    return fail(name, `unexpected throw: ${e}`);
  }
}

/** 5. generateSalt returns 32 bytes and two calls produce different values. */
async function testSaltGeneration(): Promise<TestCase> {
  const name = "generateSalt returns 32 unique bytes";
  const s1 = generateSalt();
  const s2 = generateSalt();
  const ok =
    s1.length === 32 &&
    s2.length === 32 &&
    !s1.every((b, i) => b === s2[i]);
  return ok
    ? pass(name)
    : fail(name, `s1.len=${s1.length} s2.len=${s2.length} equal=${s1.every((b, i) => b === s2[i])}`);
}

/** 6. DEFAULT_PARAMS uses PBKDF2-SHA256 at 600 000 iterations. */
async function testDefaultParams(): Promise<TestCase> {
  const name = "DEFAULT_PARAMS is PBKDF2-SHA256 at 600_000 iterations";
  const ok =
    DEFAULT_PARAMS.algorithm === "PBKDF2-SHA256" &&
    DEFAULT_PARAMS.iterations === 600_000;
  return ok
    ? pass(name)
    : fail(name, `algorithm=${DEFAULT_PARAMS.algorithm} iterations=${DEFAULT_PARAMS.iterations}`);
}

// ---------------------------------------------------------------------------
// B. Lockout policy (cases 7–10)
// ---------------------------------------------------------------------------

/** 7. nextLockoutMs(0..4) → null. */
async function testLockoutUnder5(): Promise<TestCase> {
  const name = "nextLockoutMs(0..4) → null";
  for (let i = 0; i <= 4; i++) {
    if (nextLockoutMs(i) !== null) {
      return fail(name, `attempts=${i} returned non-null`);
    }
  }
  return pass(name);
}

/** 8. nextLockoutMs(5..7) → 30_000 ms. */
async function testLockout5to7(): Promise<TestCase> {
  const name = "nextLockoutMs(5..7) → 30_000 ms";
  for (let i = 5; i <= 7; i++) {
    if (nextLockoutMs(i) !== 30_000) {
      return fail(name, `attempts=${i} returned ${nextLockoutMs(i)}`);
    }
  }
  return pass(name);
}

/** 9. nextLockoutMs(8..9) → 300_000 ms. */
async function testLockout8to9(): Promise<TestCase> {
  const name = "nextLockoutMs(8..9) → 300_000 ms";
  for (let i = 8; i <= 9; i++) {
    if (nextLockoutMs(i) !== 300_000) {
      return fail(name, `attempts=${i} returned ${nextLockoutMs(i)}`);
    }
  }
  return pass(name);
}

/** 10. nextLockoutMs(10..11) → 1_800_000 ms. */
async function testLockout10to11(): Promise<TestCase> {
  const name = "nextLockoutMs(10..11) → 1_800_000 ms";
  for (let i = 10; i <= 11; i++) {
    if (nextLockoutMs(i) !== 1_800_000) {
      return fail(name, `attempts=${i} returned ${nextLockoutMs(i)}`);
    }
  }
  return pass(name);
}

// ---------------------------------------------------------------------------
// C. State machine (cases 11–21)
// ---------------------------------------------------------------------------

/** 11. unlocked + sessionEnded → lockedRequirePassphrase. */
async function testSessionEnded(): Promise<TestCase> {
  const name = "unlocked + sessionEnded → lockedRequirePassphrase";
  const nowMs = 1_000_000;
  const unlocked: AppLockStatus = { state: "unlocked", history: ZERO_HISTORY, lastUnlockedAt: nowMs };
  const next = transition(unlocked, { type: "sessionEnded" }, nowMs);
  return next.state === "lockedRequirePassphrase"
    ? pass(name)
    : fail(name, `got state=${next.state}`);
}

/** 12. lockedRequirePassphrase + correctPassphrase → unlocked. */
async function testCorrectPassphraseUnlocks(): Promise<TestCase> {
  const name = "lockedRequirePassphrase + correctPassphrase → unlocked";
  const then = 1_000_000;
  const now  = 2_000_000;
  const locked: AppLockStatus = {
    state: "lockedRequirePassphrase",
    history: { attempts: 4, lastFailedAt: then, lockoutUntil: null },
    lastUnlockedAt: then,
  };
  const next = transition(locked, { type: "userEnteredCorrectPassphrase" }, now);
  const ok =
    next.state === "unlocked" &&
    next.history.attempts === 0 &&
    next.history.lockoutUntil === null &&
    next.lastUnlockedAt === now;
  return ok
    ? pass(name)
    : fail(name, `state=${next.state} attempts=${next.history.attempts} lastUnlock=${next.lastUnlockedAt}`);
}

/** 13. 5 wrong passphrases → lockedRequirePassphrase with 30s lockout. */
async function testWrong5Triggers30s(): Promise<TestCase> {
  const name = "5 wrong passphrases → 30s lockout, still lockedRequirePassphrase";
  const nowMs = 1_000_000;
  setBackgroundLockMs(60_000);
  let status: AppLockStatus = {
    state: "lockedRequirePassphrase",
    history: ZERO_HISTORY,
    lastUnlockedAt: nowMs,
  };
  for (let i = 0; i < 5; i++) {
    status = transition(status, { type: "userEnteredWrongPassphrase" }, nowMs);
  }
  const ok =
    status.history.attempts === 5 &&
    status.history.lockoutUntil === nowMs + 30_000 &&
    status.state === "lockedRequirePassphrase";
  return ok
    ? pass(name)
    : fail(name, `attempts=${status.history.attempts} lockoutUntil=${status.history.lockoutUntil} state=${status.state}`);
}

/** 14. 8 wrong passphrases → 300s lockout. */
async function testWrong8Triggers300s(): Promise<TestCase> {
  const name = "8 wrong passphrases → 300s lockout";
  const nowMs = 1_000_000;
  let status: AppLockStatus = {
    state: "lockedRequirePassphrase",
    history: ZERO_HISTORY,
    lastUnlockedAt: nowMs,
  };
  for (let i = 0; i < 8; i++) {
    status = transition(status, { type: "userEnteredWrongPassphrase" }, nowMs);
  }
  const ok =
    status.history.attempts === 8 &&
    status.history.lockoutUntil === nowMs + 300_000;
  return ok
    ? pass(name)
    : fail(name, `attempts=${status.history.attempts} lockoutUntil=${status.history.lockoutUntil}`);
}

/** 15. 10 wrong passphrases → 1800s lockout. */
async function testWrong10Triggers1800s(): Promise<TestCase> {
  const name = "10 wrong passphrases → 1800s lockout";
  const nowMs = 1_000_000;
  let status: AppLockStatus = {
    state: "lockedRequirePassphrase",
    history: ZERO_HISTORY,
    lastUnlockedAt: nowMs,
  };
  for (let i = 0; i < 10; i++) {
    status = transition(status, { type: "userEnteredWrongPassphrase" }, nowMs);
  }
  const ok =
    status.history.attempts === 10 &&
    status.history.lockoutUntil === nowMs + 1_800_000;
  return ok
    ? pass(name)
    : fail(name, `attempts=${status.history.attempts} lockoutUntil=${status.history.lockoutUntil}`);
}

/** 16. 12 wrong passphrases → wipePending (terminal). */
async function testWrong12WipePending(): Promise<TestCase> {
  const name = "12 wrong passphrases → wipePending";
  const nowMs = 1_000_000;
  let status: AppLockStatus = {
    state: "lockedRequirePassphrase",
    history: ZERO_HISTORY,
    lastUnlockedAt: nowMs,
  };
  for (let i = 0; i < 12; i++) {
    status = transition(status, { type: "userEnteredWrongPassphrase" }, nowMs);
  }
  const ok = status.state === "wipePending" && status.history.attempts === 12;
  return ok
    ? pass(name)
    : fail(name, `state=${status.state} attempts=${status.history.attempts}`);
}

/** 17. Biometric failure → lockedRequirePassphraseAfterBiometricFailure. */
async function testBiometricFailureEscalates(): Promise<TestCase> {
  const name = "biometricFailedThresholdReached escalates to lockedRequirePassphraseAfterBiometricFailure";
  const nowMs = 1_000_000;
  const locked: AppLockStatus = {
    state: "lockedRequirePassphrase",
    history: ZERO_HISTORY,
    lastUnlockedAt: nowMs,
  };
  const next = transition(locked, { type: "biometricFailedThresholdReached" }, nowMs);
  return next.state === "lockedRequirePassphraseAfterBiometricFailure"
    ? pass(name)
    : fail(name, `got state=${next.state}`);
}

/** 18. Biometric success ignored from lockedRequirePassphraseAfterBiometricFailure. */
async function testBiometricIgnoredAfterFailure(): Promise<TestCase> {
  const name = "biometricSucceeded ignored from lockedRequirePassphraseAfterBiometricFailure";
  const nowMs = 1_000_000;
  const locked: AppLockStatus = {
    state: "lockedRequirePassphraseAfterBiometricFailure",
    history: ZERO_HISTORY,
    lastUnlockedAt: nowMs,
  };
  const next = transition(locked, { type: "biometricSucceeded" }, nowMs);
  return next.state === "lockedRequirePassphraseAfterBiometricFailure"
    ? pass(name)
    : fail(name, `got state=${next.state}`);
}

/** 19. 24h since last passphrase → biometric ignored, passphrase required. */
async function testBiometricIgnoredAfter24h(): Promise<TestCase> {
  const name = "biometric ignored after 24h since last passphrase";
  const then  = 1_000_000;
  const after = then + BIOMETRIC_24H_WINDOW_MS + 1;
  const locked: AppLockStatus = {
    state: "lockedRequirePassphrase",
    history: ZERO_HISTORY,
    lastUnlockedAt: then,
  };
  const next = transition(locked, { type: "biometricSucceeded" }, after);
  return next.state === "lockedRequirePassphrase"
    ? pass(name)
    : fail(name, `got state=${next.state}`);
}

/** 20. Biometric success within 24h unlocks (lastUnlockedAt unchanged). */
async function testBiometricWithin24hUnlocks(): Promise<TestCase> {
  const name = "biometricSucceeded within 24h unlocks (lastUnlockedAt unchanged)";
  const then = 1_000_000;
  const now  = then + 3_600_000; // 1h later, well within 24h
  const locked: AppLockStatus = {
    state: "lockedRequirePassphrase",
    history: ZERO_HISTORY,
    lastUnlockedAt: then,
  };
  const next = transition(locked, { type: "biometricSucceeded" }, now);
  const ok = next.state === "unlocked" && next.lastUnlockedAt === then;
  return ok
    ? pass(name)
    : fail(name, `state=${next.state} lastUnlockedAt=${next.lastUnlockedAt} (want ${then})`);
}

/** 21. wipePending terminal: every event keeps state as wipePending. */
async function testWipePendingIsTerminal(): Promise<TestCase> {
  const name = "wipePending is terminal under every event";
  const nowMs = 1_000_000;
  const wipe: AppLockStatus = {
    state: "wipePending",
    history: { attempts: 12, lastFailedAt: nowMs, lockoutUntil: null },
    lastUnlockedAt: nowMs,
  };
  const events: AppLockEvent[] = [
    { type: "userEnteredCorrectPassphrase" },
    { type: "userEnteredWrongPassphrase" },
    { type: "biometricSucceeded" },
    { type: "biometricFailedThresholdReached" },
    { type: "appBackgrounded" },
    { type: "appForegroundedAfterMs", elapsedMs: 120_000 },
    { type: "configBackgroundLockMs", ms: 60_000 },
    { type: "sessionEnded" },
  ];
  for (const e of events) {
    const next = transition(wipe, e, nowMs);
    if (next.state !== "wipePending") {
      return fail(name, `event=${e.type} → state=${next.state}`);
    }
  }
  return pass(name);
}

// ---------------------------------------------------------------------------
// D. Store — IndexedDB persistence (cases 22–25)
// ---------------------------------------------------------------------------

/** 22. setPassphrase then currentHash returns a hash that verifies. */
async function testStoreSetThenRead(): Promise<TestCase> {
  const name = "store: setPassphrase then currentHash verifies";
  const store = freshStore();
  try {
    await store.init();
    await store.setPassphrase("hunter2!!", new Date(5000));
    const h = await store.currentHash();
    if (!h) return fail(name, "currentHash returned undefined");
    const ok = await verifyPassphrase("hunter2!!", h);
    return ok ? pass(name) : fail(name, "verifyPassphrase returned false on persisted hash");
  } catch (e) {
    return fail(name, String(e));
  }
}

/** 23. clearPassphrase removes the stored hash. */
async function testStoreClear(): Promise<TestCase> {
  const name = "store: clearPassphrase removes hash";
  const store = freshStore();
  try {
    await store.init();
    await store.setPassphrase("hunter2!!");
    await store.clearPassphrase();
    const h = await store.currentHash();
    return h === undefined ? pass(name) : fail(name, "hash still present after clear");
  } catch (e) {
    return fail(name, String(e));
  }
}

/** 24. updateStatus persists and survives a fresh store at the same dbName. */
async function testStoreStatusPersists(): Promise<TestCase> {
  const name = "store: updateStatus persists across instances";
  const dbName = `kaether_applock_test_persist_${Date.now()}`;
  try {
    const writer = new AppLockStore(dbName);
    await writer.init();
    const want: AppLockStatus = {
      state: "lockedRequirePassphraseAfterBiometricFailure",
      history: { attempts: 7, lastFailedAt: 5_000, lockoutUntil: 35_000 },
      lastUnlockedAt: 5_000,
    };
    await writer.updateStatus(want);

    const reader = new AppLockStore(dbName);
    await reader.init();
    const got = await reader.currentStatus();

    const ok =
      got.state === want.state &&
      got.history.attempts === want.history.attempts &&
      got.history.lockoutUntil === want.history.lockoutUntil &&
      got.lastUnlockedAt === want.lastUnlockedAt;
    return ok ? pass(name) : fail(name, `got=${JSON.stringify(got)} want=${JSON.stringify(want)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

/** 25. currentBackgroundLockMs defaults to 60_000. */
async function testStoreDefaultConfig(): Promise<TestCase> {
  const name = "store: backgroundLockMs defaults to 60_000";
  const store = freshStore();
  try {
    await store.init();
    const ms = await store.currentBackgroundLockMs();
    return ms === 60_000 ? pass(name) : fail(name, `got ${ms}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// E. Hardening (cases 26–27)
// ---------------------------------------------------------------------------

/**
 * 26. Constant-time verify: wrong-length inputs return false, don't crash or
 * leak length through thrown exceptions.
 */
async function testConstantTimeVerify(): Promise<TestCase> {
  const name = "constant-time verify: wrong-length inputs return false, no throw";
  try {
    const salt = generateSalt();
    const h = await hashPassphrase("correct", salt, FAST_PARAMS);
    // Too short
    const v1 = await verifyPassphrase("corr", h);   // 4 chars — below 6
    // Too long
    const v2 = await verifyPassphrase("a".repeat(65), h); // 65 chars — above 64
    // Wrong but valid length
    const v3 = await verifyPassphrase("wrongg", h);
    const ok = !v1 && !v2 && !v3;
    return ok ? pass(name) : fail(name, `v1=${v1} v2=${v2} v3=${v3}`);
  } catch (e) {
    return fail(name, `unexpected throw: ${e}`);
  }
}

/**
 * 27. Algorithm discriminator field is stored: record with `algorithm:
 * "PBKDF2-SHA256"` and wrong iterations still goes through verify (the
 * verifier uses the stored `iterations` field).
 */
async function testAlgorithmDiscriminatorOnDisk(): Promise<TestCase> {
  const name = "algorithm discriminator stored on disk; iterations from record";
  try {
    const salt = generateSalt();
    const h = await hashPassphrase("secret!", salt, { algorithm: "PBKDF2-SHA256", iterations: 1_000 });
    // Verify must use h.iterations (1000) not DEFAULT_PARAMS.iterations (600k)
    const ok = await verifyPassphrase("secret!", h);
    return ok ? pass(name) : fail(name, "verify with stored iterations returned false");
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// F. Algorithm guard (case 28)
// ---------------------------------------------------------------------------

/**
 * 28. A record with `algorithm: "Argon2id"` (future/unknown) → throws
 * UnsupportedAlgorithmError explicitly, not a silent wrong-answer.
 */
async function testUnsupportedAlgorithm(): Promise<TestCase> {
  const name = 'record with algorithm:"Argon2id" → UnsupportedAlgorithmError';
  try {
    const salt = generateSalt();
    const h = await hashPassphrase("passphrase123", salt, FAST_PARAMS);
    // Forge a future-algorithm record
    const forged: AppLockPassphraseHash = { ...h, algorithm: "Argon2id" as never };
    try {
      await verifyPassphrase("passphrase123", forged);
      return fail(name, "verifyPassphrase did not throw on unsupported algorithm");
    } catch (e) {
      if (e instanceof UnsupportedAlgorithmError) return pass(name);
      return fail(name, `expected UnsupportedAlgorithmError, got ${e}`);
    }
  } catch (e) {
    return fail(name, `setup failed: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "App lock (passphrase + WebAuthn + throttling)",
  isVerified: true,
  run: async () =>
    buildSuiteResult(
      await Promise.all([
        // A. Passphrase hash
        testHashRoundTrip(),
        testTooShort(),
        testTooLong(),
        testWrongPassphrase(),
        testSaltGeneration(),
        testDefaultParams(),
        // B. Lockout policy
        testLockoutUnder5(),
        testLockout5to7(),
        testLockout8to9(),
        testLockout10to11(),
        // C. State machine
        testSessionEnded(),
        testCorrectPassphraseUnlocks(),
        testWrong5Triggers30s(),
        testWrong8Triggers300s(),
        testWrong10Triggers1800s(),
        testWrong12WipePending(),
        testBiometricFailureEscalates(),
        testBiometricIgnoredAfterFailure(),
        testBiometricIgnoredAfter24h(),
        testBiometricWithin24hUnlocks(),
        testWipePendingIsTerminal(),
        // D. Store
        testStoreSetThenRead(),
        testStoreClear(),
        testStoreStatusPersists(),
        testStoreDefaultConfig(),
        // E. Hardening
        testConstantTimeVerify(),
        testAlgorithmDiscriminatorOnDisk(),
        // F. Algorithm guard
        testUnsupportedAlgorithm(),
      ]),
    ),
});
